/**
 * Toggle "Ativar push neste dispositivo" usado dentro do sheet de
 * Preferências de Notificação. Estado refletido a partir da inscrição
 * real do dispositivo (PushSubscription + token FCM), não apenas da
 * permissão do navegador — que permanece 'granted' após desativar.
 *
 * @version 1.2.0 - 2026-06-05 — Estado derivado de isDeviceSubscribed() em vez de Notification.permission
 * @version 1.1.0 - 2026-06-05 — Substituído Switch por IconToggle (padrão Departamentos)
 */
import { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, AlertCircle } from 'lucide-react';
import { IconToggle } from '@/components/ui/icon-toggle';
import { useAuth } from '@/hooks/useAuth';
import {
  disablePush,
  enablePush,
  getPermissionState,
  isDeviceSubscribed,
  isPushSupported,
} from '@/lib/fcm';

export function PushNotificationToggle() {
  const { currentTenant } = useAuth();
  const [supported, setSupported] = useState<boolean | null>(null);
  // null = ainda carregando; true/false = estado real da inscrição
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [busy, setBusy] = useState(false);

  // Inicializa o estado a partir da inscrição real (sobrevive a F5)
  useEffect(() => {
    let active = true;
    (async () => {
      const ok = await isPushSupported();
      if (!active) return;
      setSupported(ok);
      setPermission(getPermissionState());
      if (ok) {
        const sub = await isDeviceSubscribed();
        if (!active) return;
        setSubscribed(sub);
      } else {
        setSubscribed(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleToggle = useCallback(async (value: boolean) => {
    setBusy(true);
    try {
      if (value) {
        await enablePush(currentTenant?.id ?? null);
      } else {
        await disablePush();
      }
      // Reconsulta a fonte de verdade após a operação (independente de sucesso parcial)
      setPermission(getPermissionState());
      setSubscribed(await isDeviceSubscribed());
    } finally {
      setBusy(false);
    }
  }, [currentTenant?.id]);

  if (supported === null || subscribed === null) {
    return <div className="h-9 rounded-lg bg-muted/40 animate-pulse" />;
  }

  if (!supported) {
    return (
      <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-muted/30">
        <AlertCircle className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
        <p className="text-[11px] leading-snug text-muted-foreground">
          Este navegador não suporta push. Em iPhone, instale o app na tela inicial.
        </p>
      </div>
    );
  }

  const denied = permission === 'denied';
  const enabled = subscribed === true;

  return (
    <div className="space-y-1.5">
      <div
        className="flex items-center justify-between px-2.5 py-2 rounded-lg hover:bg-muted/60 transition-colors"
        title={denied ? 'Permissão bloqueada nas configurações do navegador' : undefined}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {enabled ? (
            <Bell className="h-3.5 w-3.5 text-primary shrink-0" />
          ) : (
            <BellOff className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          <span className="text-[13px] font-medium text-foreground/90 truncate">
            Push neste dispositivo
          </span>
        </div>
        <IconToggle
          checked={enabled}
          disabled={busy || denied}
          onCheckedChange={handleToggle}
          aria-label="Ativar push neste dispositivo"
          tooltipOn="Desativar push"
          tooltipOff="Ativar push"
        />
      </div>
      {denied && (
        <p className="px-2.5 text-[10px] leading-snug text-amber-600">
          Permissão bloqueada — libere notificações nas configurações do navegador.
        </p>
      )}
    </div>
  );
}
